import React, { useState, useMemo } from 'react';
import { getOptimizedImageUrl } from '../utils/conversion';
import '../styles/ListPanel.css';

function ListPanel({ elements, onSelectElement, onClose, selectedElement, onNavigateToWallPlanner }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedWalls, setExpandedWalls] = useState(new Set()); // Track which walls are expanded

  // Group elements by walls and other elements
  const groupedElements = useMemo(() => {
    if (!elements) return { walls: [], other: [], pods: [], easels: [] };

    const walls = elements.filter(el => el.type === 'wall_2d');
    const easels = elements.filter(el => el.type === 'easel');
    const other = elements.filter(el => el.type !== 'wall_2d' && el.type !== 'easel');
    
    // Identify pod groups from wall text_content metadata
    const podMap = {};
    const standAloneWalls = [];
    walls.forEach(wall => {
      let podGroup = null;
      try {
        const meta = wall.text_content ? JSON.parse(wall.text_content) : null;
        if (meta?.pod_group) podGroup = meta.pod_group;
      } catch { /* not a pod wall */ }
      
      if (podGroup) {
        if (!podMap[podGroup]) podMap[podGroup] = { name: podGroup, walls: [] };
        podMap[podGroup].walls.push(wall);
      } else {
        standAloneWalls.push(wall);
      }
    });
    
    // Group artwork under their parent walls (including pod walls)
    const allWallsWithArtwork = walls.map(wall => {
      const wallArtwork = other.filter(el => 
        el.type === 'artwork' && el.parent_element_id === wall.element_id
      );
      return { ...wall, artwork: wallArtwork };
    });
    
    const standAloneWallsWithArtwork = standAloneWalls.map(wall => 
      allWallsWithArtwork.find(w => w.element_id === wall.element_id) || { ...wall, artwork: [] }
    );
    
    // Build pod groups with artwork counts
    const pods = Object.values(podMap).map(pod => ({
      ...pod,
      walls: pod.walls.map(wall => 
        allWallsWithArtwork.find(w => w.element_id === wall.element_id) || { ...wall, artwork: [] }
      )
    }));

    // Get unplaced artwork (not on any wall or easel)
    const unplacedArtwork = other.filter(el => 
      el.type === 'artwork' && !el.parent_element_id
    );
    
    // Easels with their artwork
    const easelsWithArtwork = easels.map(easel => {
      const easelArtwork = other.filter(el => 
        el.type === 'artwork' && el.parent_element_id === easel.element_id
      );
      return { ...easel, artwork: easelArtwork };
    });

    // Sort walls alphabetically by name (source field)
    const sortedWalls = standAloneWallsWithArtwork.sort((a, b) => {
      const nameA = (a.source || '').toLowerCase();
      const nameB = (b.source || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return {
      walls: sortedWalls,
      pods,
      easels: easelsWithArtwork,
      unplacedArtwork,
      other: other.filter(el => el.type !== 'artwork') // Non-artwork, non-wall, non-easel elements
    };
  }, [elements]);

  // Filter and sort elements based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedElements;

    const searchLower = searchTerm.toLowerCase();
    
    // Filter walls that match search
    const filteredWalls = groupedElements.walls.filter(wall => 
      wall.source?.toLowerCase().includes(searchLower) ||
      wall.artwork.some(art => 
        art.vc_id?.toLowerCase().includes(searchLower) ||
        art.title?.toLowerCase().includes(searchLower)
      )
    );

    // Filter unplaced artwork
    const filteredUnplaced = groupedElements.unplacedArtwork.filter(art => 
      art.vc_id?.toLowerCase().includes(searchLower) ||
      art.title?.toLowerCase().includes(searchLower)
    );

    // Filter other elements
    const filteredOther = groupedElements.other.filter(el => 
      el.vc_id?.toLowerCase().includes(searchLower) ||
      el.title?.toLowerCase().includes(searchLower)
    );

    // Filter pods
    const filteredPods = (groupedElements.pods || []).filter(pod =>
      pod.name?.toLowerCase().includes(searchLower) ||
      pod.walls.some(wall =>
        wall.source?.toLowerCase().includes(searchLower) ||
        wall.artwork.some(art =>
          art.vc_id?.toLowerCase().includes(searchLower) ||
          art.title?.toLowerCase().includes(searchLower)
        )
      )
    );

    // Filter easels
    const filteredEasels = (groupedElements.easels || []).filter(easel =>
      easel.source?.toLowerCase().includes(searchLower) ||
      easel.artwork?.some(art =>
        art.vc_id?.toLowerCase().includes(searchLower) ||
        art.title?.toLowerCase().includes(searchLower)
      )
    );

    // Sort filtered walls alphabetically by name
    const sortedFilteredWalls = filteredWalls.sort((a, b) => {
      const nameA = (a.source || '').toLowerCase();
      const nameB = (b.source || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return {
      walls: sortedFilteredWalls,
      pods: filteredPods,
      easels: filteredEasels,
      unplacedArtwork: filteredUnplaced,
      other: filteredOther
    };
  }, [groupedElements, searchTerm]);

  // Toggle wall expansion
  const toggleWallExpansion = (wallId) => {
    setExpandedWalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wallId)) {
        newSet.delete(wallId);
      } else {
        newSet.add(wallId);
      }
      return newSet;
    });
  };

  // Render thumbnail based on element type
  const renderThumbnail = (element) => {
    switch (element.type) {
      case 'wall_2d':
        return null; // No thumbnail for walls
      case 'box':
        return (
          <div 
            className="list-panel-item-thumbnail"
            style={{ 
              backgroundColor: element.color || '#808080',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white'
            }}
          >
            Box
          </div>
        );
      case 'easel':
        return (
          <div 
            className="list-panel-item-thumbnail"
            style={{ 
              backgroundColor: element.color || '#8B4513',
              opacity: element.opacity || 0.7,
              clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)'
            }}
          />
        );
        case 'furniture':
          return (
            <div 
              className="list-panel-item-thumbnail"
              style={{ 
                backgroundColor: '#A0522D',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white'
              }}
            >
              Furniture
            </div>
          );

      default:
        // Artwork or default
        const thumbnailUrl = element.source 
          ? getOptimizedImageUrl(element.source, 'thumbnail')
          : null;
        
        return thumbnailUrl ? (
          <div className="list-panel-item-thumbnail">
            <img 
              src={thumbnailUrl} 
              alt={element.vc_id || 'Artwork'} 
            />
          </div>
        ) : null;
    }
  };

  // Render details based on element type
  const renderElementDetails = (element) => {
    switch (element.type) {
      case 'wall_2d':
        return (
          <>
            <div className="list-panel-item-title">
              {element.source || 'Wall'}
            </div>
            <div className="list-panel-item-info">
              {element.width}" × {element.height}" ({element.artwork?.length || 0} artworks)
            </div>
          </>
        );
      case 'box':
        return (
          <>
            <div className="list-panel-item-title">Box</div>
            <div className="list-panel-item-info">
              {element.width}" × {element.height}" × {element.depth}"
            </div>
          </>
        );
      case 'easel':
        return (
          <>
            <div className="list-panel-item-title">{element.source || 'Easel'}</div>
            <div className="list-panel-item-info">
              {element.width}" × {element.height}" ({element.artwork?.length || 0} artworks)
            </div>
          </>
        );

      case 'furniture':
          return (
            <>
              <div className="list-panel-item-title">
                Furniture
              </div>
              <div className="list-panel-item-info">
                {element.width}" × {element.height}" × {element.depth}"
              </div>
            </>
          );
      default:
        return (
          <>
            <div className="list-panel-item-title">
              {element.vc_id || 'Artwork'}
            </div>
            {element.title && (
              <div className="list-panel-item-subtitle">
                {element.title.length > 30 ? `${element.title.substring(0, 30)}...` : element.title}
              </div>
            )}
            <div className="list-panel-item-info">
              {element.width}" × {element.height}"
            </div>
          </>
        );
    }
  };

  // Render a single element item
  // isChild: true if artwork is nested under a wall
  // isUnplaced: true if artwork is in the unplaced section (no 3D representation)
  const renderElementItem = (element, isChild = false, isUnplaced = false) => {
    const isSelected = selectedElement && (
      selectedElement.element_id === element.element_id ||
      (selectedElement.vc_id && element.vc_id && selectedElement.vc_id === element.vc_id)
    );
    
    const hasThumbnail = element.type !== 'wall_2d';
    
    // Unplaced artwork has no 3D representation, so no camera focus available
    const canFocusCamera = !isUnplaced && element.type !== 'wall_2d';
    
    return (
      <div
        key={element.vc_id || element.element_id}
        className={`list-panel-item ${isSelected ? 'list-panel-item-selected' : ''} ${isChild ? 'list-panel-item-child' : ''} ${!hasThumbnail ? 'no-thumbnail' : ''}`}
        onClick={(e) => {
          // Only move camera if element has 3D representation
          if (canFocusCamera) {
            onSelectElement(element, e.ctrlKey || e.metaKey, true);
          } else if (element.type === 'wall_2d') {
            onSelectElement(element, e.ctrlKey || e.metaKey, false); // walls - no camera move
          } else {
            onSelectElement(element, e.ctrlKey || e.metaKey, false); // unplaced - no camera move
          }
        }}
        style={isChild ? { marginLeft: '20px', borderLeft: '2px solid #ddd' } : {}}
      >
        {renderThumbnail(element)}
        
        <div className="list-panel-item-details">
          {renderElementDetails(element)}
        </div>
        
        {/* Only show Select button if element can be focused in 3D view */}
        {(element.type === 'wall_2d' || canFocusCamera) && (
          <div className="list-panel-item-actions">
            <button 
              className="list-panel-item-action"
              onClick={(e) => {
                e.stopPropagation();
                if (canFocusCamera) {
                  onSelectElement(element, e.ctrlKey || e.metaKey, true);
                } else {
                  onSelectElement(element, e.ctrlKey || e.metaKey, false);
                }
              }}
            >
              Select
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render empty state if no elements
  const renderEmptyState = () => {
    return (
      <div className="list-panel-empty-state">
        <p>No items found</p>
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="list-panel-item-action"
          >
            Clear Search
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="list-panel">
      <div className="list-panel-header">
        <div className="list-panel-title">📷 Camera Controls</div>
        <div className="list-panel-header-actions">
          <button 
            className="list-panel-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
      </div>

      {/* Camera Controls Section */}
      <div className="list-panel-camera-controls">
        {/* Camera Movement */}
        <div className="camera-movement-controls">
          <div className="camera-control-label">Movement:</div>
          <div className="camera-arrow-grid">
            <button
              onClick={() => {
                let camera = document.querySelector('#camera');
                if (!camera) camera = document.querySelector('[camera]');
                if (!camera) camera = document.querySelector('a-camera');
                
                if (camera) {
                  const pos = camera.getAttribute('position');
                  let x, y, z;
                  if (typeof pos === 'string') {
                    [x, y, z] = pos.split(' ').map(Number);
                  } else if (pos && pos.x !== undefined) {
                    x = pos.x;
                    y = pos.y;
                    z = pos.z;
                  } else {
                    x = 0; y = 5; z = 20;
                  }
                  // Move forward (decrease Z)
                  if (window.moveCamera) {
                    window.moveCamera(`${x} ${y} ${z - 5}`, '0 0 0');
                  }
                }
              }}
              className="camera-arrow-btn"
              title="Move Forward"
            >
              ↑
            </button>
            <button
              onClick={() => {
                let camera = document.querySelector('#camera');
                if (!camera) camera = document.querySelector('[camera]');
                if (!camera) camera = document.querySelector('a-camera');
                
                if (camera) {
                  const pos = camera.getAttribute('position');
                  let x, y, z;
                  if (typeof pos === 'string') {
                    [x, y, z] = pos.split(' ').map(Number);
                  } else if (pos && pos.x !== undefined) {
                    x = pos.x;
                    y = pos.y;
                    z = pos.z;
                  } else {
                    x = 0; y = 5; z = 20;
                  }
                  // Move left (decrease X)
                  if (window.moveCamera) {
                    window.moveCamera(`${x - 5} ${y} ${z}`, '0 0 0');
                  }
                }
              }}
              className="camera-arrow-btn"
              title="Move Left"
            >
              ←
            </button>
            <button
              onClick={() => {
                let camera = document.querySelector('#camera');
                if (!camera) camera = document.querySelector('[camera]');
                if (!camera) camera = document.querySelector('a-camera');
                
                if (camera) {
                  const pos = camera.getAttribute('position');
                  let x, y, z;
                  if (typeof pos === 'string') {
                    [x, y, z] = pos.split(' ').map(Number);
                  } else if (pos && pos.x !== undefined) {
                    x = pos.x;
                    y = pos.y;
                    z = pos.z;
                  } else {
                    x = 0; y = 5; z = 20;
                  }
                  // Move right (increase X)
                  if (window.moveCamera) {
                    window.moveCamera(`${x + 5} ${y} ${z}`, '0 0 0');
                  }
                }
              }}
              className="camera-arrow-btn"
              title="Move Right"
            >
              →
            </button>
            <button
              onClick={() => {
                let camera = document.querySelector('#camera');
                if (!camera) camera = document.querySelector('[camera]');
                if (!camera) camera = document.querySelector('a-camera');
                
                if (camera) {
                  const pos = camera.getAttribute('position');
                  let x, y, z;
                  if (typeof pos === 'string') {
                    [x, y, z] = pos.split(' ').map(Number);
                  } else if (pos && pos.x !== undefined) {
                    x = pos.x;
                    y = pos.y;
                    z = pos.z;
                  } else {
                    x = 0; y = 5; z = 20;
                  }
                  // Move back (increase Z)
                  if (window.moveCamera) {
                    window.moveCamera(`${x} ${y} ${z + 5}`, '0 0 0');
                  }
                }
              }}
              className="camera-arrow-btn"
              title="Move Back"
            >
              ↓
            </button>
          </div>
        </div>

        {/* Camera Presets */}
        <div className="camera-preset-controls">
          <div className="camera-control-label">Preset Views:</div>
          <div className="camera-preset-grid">
            {[
              { name: 'Front', icon: '🏛️', position: '0 5 20', rotation: '0 0 0' },
              { name: 'Curator', icon: '👁️', position: '10 8 15', rotation: '0 -30 0' },
              { name: 'Overview', icon: '👁️', position: '0 15 0', rotation: '-90 0 0' },
              { name: 'Top Down', icon: '⬇️', position: '0 25 0', rotation: '-90 0 0' },
              { name: 'Detail', icon: '🔍', position: '0 3 8', rotation: '0 0 0' },
              { name: 'Side', icon: '👁️', position: '20 5 0', rotation: '0 90 0' }
            ].map(preset => (
              <button
                key={preset.name}
                onClick={() => {
                  if (window.moveCamera) {
                    window.moveCamera(preset.position, preset.rotation);
                  }
                }}
                className="camera-preset-btn"
                title={`${preset.name}: ${preset.position}`}
              >
                <span>{preset.icon}</span>
                <span>{preset.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Test Camera Positions */}
        <div className="camera-test-controls">
          <div className="camera-control-label">Test Positions:</div>
          <div className="camera-test-grid">
            <button
              onClick={() => {
                if (window.moveCamera) {
                  console.log('🧪 Testing camera position: 0 5 10');
                  window.moveCamera('0 5 10', '0 0 0');
                }
              }}
              className="camera-test-btn"
              title="Test close position"
            >
              🧪 Close
            </button>
            <button
              onClick={() => {
                if (window.moveCamera) {
                  console.log('🧪 Testing camera position: 0 5 30');
                  window.moveCamera('0 5 30', '0 0 0');
                }
              }}
              className="camera-test-btn"
              title="Test far position"
            >
              🧪 Far
            </button>
          </div>
        </div>
      </div>

      <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#333', marginTop: '20px' }}>
        Gallery Items
      </div>

      <input 
        type="text"
        placeholder="Search items..."
        className="list-panel-search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filteredGroups.walls.length === 0 && 
       (filteredGroups.pods || []).length === 0 &&
       (filteredGroups.easels || []).length === 0 &&
       filteredGroups.unplacedArtwork.length === 0 && 
       filteredGroups.other.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="list-panel-content">
          {/* Render pods */}
          {(filteredGroups.pods || []).length > 0 && (
            <div className="list-panel-section">
              <div className="list-panel-section-header">🏗️ Pods</div>
              {filteredGroups.pods.map(pod => {
                const totalArtwork = pod.walls.reduce((sum, w) => sum + (w.artwork?.length || 0), 0);
                return (
                  <div key={`pod-${pod.name}`} className="list-panel-wall-group" style={{ borderLeft: '3px solid #8b5cf6' }}>
                    <div style={{ padding: '8px 12px', fontWeight: '600', color: '#7c3aed', fontSize: '13px' }}>
                      {pod.name} ({pod.walls.length} panels, {totalArtwork} artworks)
                    </div>
                    {pod.walls.map(wall => (
                      <div key={wall.element_id} className="list-panel-wall-group" style={{ marginLeft: '12px' }}>
                        <div className="list-panel-wall-header">
                          {renderElementItem(wall)}
                          <button
                            className="list-panel-expand-button"
                            onClick={() => toggleWallExpansion(wall.element_id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#666', padding: '4px' }}
                          >
                            {expandedWalls.has(wall.element_id) ? '▼' : '▶'}
                          </button>
                        </div>
                        {expandedWalls.has(wall.element_id) && wall.artwork && wall.artwork.length > 0 && (
                          <div className="list-panel-wall-artwork">
                            {wall.artwork.map(artwork => renderElementItem(artwork, true))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Render standalone walls with their artwork */}
          {filteredGroups.walls.map(wall => (
            <div key={wall.element_id} className="list-panel-wall-group">
              <div className="list-panel-wall-header">
                {renderElementItem(wall)}
                <button
                  className="list-panel-expand-button"
                  onClick={() => toggleWallExpansion(wall.element_id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#666',
                    padding: '4px'
                  }}
                >
                  {expandedWalls.has(wall.element_id) ? '▼' : '▶'}
                </button>
              </div>
              
              {/* Render artwork under wall if expanded */}
              {expandedWalls.has(wall.element_id) && wall.artwork && wall.artwork.length > 0 && (
                <div className="list-panel-wall-artwork">
                  {wall.artwork.map(artwork => renderElementItem(artwork, true))}
                </div>
              )}
            </div>
          ))}

          {/* Render easels */}
          {(filteredGroups.easels || []).length > 0 && (
            <div className="list-panel-section">
              <div className="list-panel-section-header">🎨 Easels</div>
              {filteredGroups.easels.map(easel => (
                <div key={easel.element_id} className="list-panel-wall-group" style={{ borderLeft: '3px solid #d97706' }}>
                  <div className="list-panel-wall-header">
                    {renderElementItem(easel)}
                    {easel.artwork && easel.artwork.length > 0 && (
                      <button
                        className="list-panel-expand-button"
                        onClick={() => toggleWallExpansion(easel.element_id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#666', padding: '4px' }}
                      >
                        {expandedWalls.has(easel.element_id) ? '▼' : '▶'}
                      </button>
                    )}
                  </div>
                  {expandedWalls.has(easel.element_id) && easel.artwork && easel.artwork.length > 0 && (
                    <div className="list-panel-wall-artwork">
                      {easel.artwork.map(artwork => renderElementItem(artwork, true))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Render unplaced artwork */}
          {filteredGroups.unplacedArtwork.length > 0 && (
            <div className="list-panel-section">
              <div className="list-panel-section-header">Unplaced Artwork</div>
              {filteredGroups.unplacedArtwork.map(artwork => renderElementItem(artwork, false, true))}
            </div>
          )}

          {/* Render other elements */}
          {filteredGroups.other.length > 0 && (
            <div className="list-panel-section">
              <div className="list-panel-section-header">Other Elements</div>
              {filteredGroups.other.map(element => renderElementItem(element))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ListPanel;